import { supabase } from "@/integrations/supabase/client";

const GUEST_NAME_STORAGE_KEY = "zizanio-guest-name";
const GUEST_MANUAL_LOGOUT_KEY = "zizanio-guest-manual-logout-at";
const MANUAL_LOGOUT_SUPPRESS_MS = 5000;

export function markGuestManualLogout() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(GUEST_MANUAL_LOGOUT_KEY, String(Date.now()));
  } catch {
    // ignore storage issues
  }
}

export function wasGuestManualLogoutAfter(startedAt: number) {
  if (typeof window === "undefined") return false;

  try {
    const logoutAt = Number(window.localStorage.getItem(GUEST_MANUAL_LOGOUT_KEY) ?? 0);
    return Number.isFinite(logoutAt) && logoutAt > startedAt;
  } catch {
    return false;
  }
}

export function shouldSuppressAnonymousSessionAfterLogout() {
  if (typeof window === "undefined") return false;

  try {
    const logoutAt = Number(window.localStorage.getItem(GUEST_MANUAL_LOGOUT_KEY) ?? 0);
    return Number.isFinite(logoutAt) && logoutAt > 0 && Date.now() - logoutAt < MANUAL_LOGOUT_SUPPRESS_MS;
  } catch {
    return false;
  }
}

function isDisplayNameTakenError(error: unknown) {
  if (!error || typeof error !== "object") return false;

  const candidate = error as { code?: string; message?: string; details?: string };
  const text = `${candidate.message ?? ""} ${candidate.details ?? ""}`.toLowerCase();

  return candidate.code === "23505" && text.includes("display_name");
}

export function isRateLimitError(error: unknown) {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { status?: number; code?: string; message?: string; name?: string };
  if (candidate.status === 429) return true;
  const text = `${candidate.code ?? ""} ${candidate.message ?? ""}`.toLowerCase();
  return (
    text.includes("rate limit") ||
    text.includes("over_request_rate_limit") ||
    text.includes("too many")
  );
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function normalizeGuestName(value: string) {
  return value.trim().replace(/\s+/g, " ");
}

export function readStoredGuestName() {
  if (typeof window === "undefined") return "";

  try {
    return normalizeGuestName(window.localStorage.getItem(GUEST_NAME_STORAGE_KEY) ?? "");
  } catch {
    return "";
  }
}

export function hasStoredGuestName() {
  return readStoredGuestName().length >= 3;
}

export function writeStoredGuestName(name: string) {
  if (typeof window === "undefined") return;

  try {
    const normalized = normalizeGuestName(name);

    if (!normalized) {
      window.localStorage.removeItem(GUEST_NAME_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(GUEST_NAME_STORAGE_KEY, normalized);
  } catch {
    // ignore storage issues
  }
}

export function clearStoredGuestName() {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(GUEST_NAME_STORAGE_KEY);
  } catch {
    // ignore storage issues
  }
}

export async function syncGuestProfileName(userId: string, displayName: string) {
  const normalized = normalizeGuestName(displayName);

  if (!userId || normalized.length < 3) return null;

  const { error } = await supabase.from("profiles").upsert(
    {
      user_id: userId,
      display_name: normalized,
      is_guest: true,
    },
    { onConflict: "user_id" }
  );

  return error ?? null;
}

export async function signInGuestWithName(displayName: string) {
  const startedAt = Date.now();
  const normalized = normalizeGuestName(displayName);

  if (normalized.length < 3) {
    return {
      data: { user: null },
      error: new Error("Το όνομα πρέπει να έχει τουλάχιστον 3 χαρακτήρες"),
    };
  }

  writeStoredGuestName(normalized);

  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) return { data, error };

  if (wasGuestManualLogoutAfter(startedAt)) {
    await supabase.auth.signOut();
    return { data: { user: null }, error: new Error("Η αποσύνδεση ολοκληρώθηκε") };
  }

  if (data.user) {
    const profileError = await syncGuestProfileName(data.user.id, normalized);

    if (profileError) {
      await supabase.auth.signOut();
      return { data, error: profileError };
    }
  }

  return { data, error: null };
}

export function generateGuestName(): string {
  const num = Math.floor(1000 + Math.random() * 9000);
  return `Guest_${num}`;
}

export async function signInGuestWithStoredName() {
  const stored = readStoredGuestName();

  const RATE_LIMIT_BACKOFFS_MS = [2000, 5000, 10000];

  const attemptOnce = async (name: string) => signInGuestWithName(name);

  let firstAttemptName = stored || generateGuestName();
  let firstAttempt = await attemptOnce(firstAttemptName);

  // Silent retry on 429 rate-limits (per user request: avoid showing signup popup).
  for (let i = 0; i < RATE_LIMIT_BACKOFFS_MS.length && isRateLimitError(firstAttempt.error); i += 1) {
    await sleep(RATE_LIMIT_BACKOFFS_MS[i]);
    firstAttempt = await attemptOnce(firstAttemptName);
  }

  if (!firstAttempt.error || !isDisplayNameTakenError(firstAttempt.error)) {
    return firstAttempt;
  }

  for (let attempt = 0; attempt < 5; attempt += 1) {
    const retry = await attemptOnce(generateGuestName());

    if (!retry.error || !isDisplayNameTakenError(retry.error)) {
      return retry;
    }
  }

  return firstAttempt;
}
